import { onUnmounted, ref, watch } from 'vue'
import { api, endpoints } from '../../services/api.js'
import { parametresFiltres } from '../useFormateursList.js'

/**
 * Les formateurs de la liste, mais pour la CARTE : tous ceux que les filtres
 * retiennent, positionnés, sans pagination.
 *
 * POURQUOI UN CHARGEMENT À PART plutôt que réutiliser celui de la liste. La
 * liste rend une PAGE (vingt lignes) ; une carte qui n'afficherait que la page
 * courante dessinerait une France amputée sans que rien ne le dise. Les deux
 * vues posent donc la même question au serveur — mêmes filtres, même clause,
 * même ciblage (cf. buildFormateursWhere et formateur-ciblage.service.js) —
 * mais n'en rapportent pas la même chose.
 *
 * IL NE FILTRE RIEN LUI-MÊME, et c'est la règle qui compte ici. La distance au
 * point de référence, la fourchette de prix et le matériel exigé s'appliquent
 * CÔTÉ SERVEUR, comme pour la liste. Les refaire ici, en JavaScript, aurait
 * donné deux implémentations d'une même règle : le jour où l'une dévie, la
 * carte montre un formateur que la liste ne compte pas, et rien ne dit
 * laquelle des deux a raison.
 *
 * IL NE CHARGE QUE QUAND ON REGARDE LA CARTE. Ouvrir la liste ne doit pas
 * coûter une seconde requête de plusieurs centaines de fiches à quelqu'un qui
 * ne basculera jamais. Le premier affichage de la carte déclenche le premier
 * chargement, et les changements de filtres faits pendant qu'on est sur la
 * liste sont rattrapés au retour (`perime`).
 *
 * LE DÉLAI, et pourquoi il existe. Les filtres portent la RECHERCHE, qui
 * s'écrit lettre par lettre, et les CURSEURS de rayon et de prix, qui glissent
 * pas à pas : sans attente, tirer une poignée lancerait trente requêtes non
 * paginées. C'est le même raisonnement que `useDebouncedListSearch` côté liste,
 * appliqué ici à tous les filtres d'un coup — sur une carte, aucun filtre n'a
 * besoin d'être instantané.
 */
const DELAI_RECHARGEMENT = 300

export function useFormateursCarte(filters, estAffichee) {
  const formateurs = ref([])
  const total = ref(0)
  const loading = ref(false)
  const error = ref(null)

  // Vrai tant que les données en mémoire ne correspondent plus aux filtres.
  let perime = true
  let minuteur = null
  // Garde de génération : une réponse lente ne doit jamais écraser une réponse
  // plus récente (mêmes mœurs que `useProgressiveList` côté liste).
  let generation = 0

  async function charger() {
    const mienne = ++generation
    loading.value = true
    error.value = null
    try {
      const data = await api.get(endpoints.formateurs.carte(parametresFiltres(filters.value)))
      if (mienne !== generation) return
      formateurs.value = data.formateurs || []
      total.value = data.total || 0
      perime = false
    } catch (err) {
      if (mienne !== generation) return
      console.error('Erreur chargement carte formateurs:', err)
      error.value = err?.message || 'La carte n\u2019a pas pu \u00eatre charg\u00e9e'
    } finally {
      if (mienne === generation) loading.value = false
    }
  }

  function chargerSiBesoin() {
    if (perime) charger()
  }

  watch(
    filters,
    () => {
      perime = true
      if (!estAffichee()) return
      clearTimeout(minuteur)
      minuteur = setTimeout(charger, DELAI_RECHARGEMENT)
    },
    { deep: true }
  )

  // Une frappe laiss\u00e9e en suspens ne doit pas lancer une requ\u00eate sur une page
  // qu'on vient de quitter.
  onUnmounted(() => clearTimeout(minuteur))

  return { formateurs, total, loading, error, charger, chargerSiBesoin }
}
